import { Box, Button, CircularProgress, Fade, Typography } from '@material-ui/core';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { acceptAgreement, getCurrentUser, getOperations } from 'api';
import keycloak, { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, ensureFreshToken } from 'keycloak';
import { pulseIdentify } from 'pulse';
import { ASetOperations, ASetUser } from 'reducers';
import UserAgreementDialog from 'dialogs/UserAgreementDialog';

export interface SessionContextState {
  keycloak: typeof keycloak | null;
  isAuthenticated: boolean;
}

// Ключи переехали в keycloak.ts, к самому токену. Реэкспорт оставлен, чтобы не править
// импорты в десятке мест, которые берут их отсюда.
export { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from 'keycloak';

export const SessionContext = createContext<SessionContextState | null>(null);

const SessionProvider = ({ children }: { children: React.ReactNode }) => {
  const [keycloakInstance, setKeycloakInstance] = useState<typeof keycloak | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [showAgreementDialog, setShowAgreementDialog] = useState(false);

  const isInitialized = useRef(false);

  const dispatch = useDispatch();

  const initKeycloak = useCallback(async () => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    try {
      const authenticated = await keycloak.init({
        checkLoginIframe: false,
        onLoad: 'login-required',
      });

      if (authenticated) {
        setKeycloakInstance(keycloak);

        if (keycloak.token) {
          localStorage.setItem(ACCESS_TOKEN_KEY, keycloak.token);
        }

        if (keycloak.refreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, keycloak.refreshToken);
        }

        const user = await getCurrentUser();

        // Связываем анонимную сессию BI Pulse с сотрудником
        pulseIdentify(user.id);

        // Проверяем, принял ли пользователь соглашение
        if (!user.agreementAccepted) {
          // Сохраняем только пользователя, операции не загружаем
          dispatch(ASetUser(user));
          setShowAgreementDialog(true);
        } else {
          // Пользователь принял соглашение - загружаем операции и даем полный доступ
          const operations = await getOperations();
          dispatch(ASetUser(user));
          dispatch(ASetOperations(operations));
        }

        setIsAuthenticated(true);
        setIsError(false);
      } else {
        setIsAuthenticated(false);

        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      }
    } catch (error) {
      setIsAuthenticated(false);
      setIsError(true);

      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    } finally {
      setIsLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAuthenticated) initKeycloak();
  }, [isAuthenticated, initKeycloak]);

  // Обновление токена. Поводов четыре, и одного таймера мало: браузер придушивает таймеры
  // в фоновых вкладках до одного срабатывания в минуту, а после пяти минут скрытости — до
  // одного в пять, при спящей же системе они не идут вовсе. Токен короче этих пауз, поэтому
  // к таймеру добавлены событийные поводы.
  useEffect(() => {
    if (!keycloakInstance) return undefined;

    // ensureFreshToken разбирается с ошибками сам и никогда не отклоняется
    const refresh = () => {
      ensureFreshToken();
    };

    // Возврат к вкладке: обновляемся до того, как пользователь что-нибудь нажмёт
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    const refreshInterval = setInterval(refresh, 60 * 1000);

    // Срабатывает в момент истечения — страхует случай, когда таймер задушили или проспали
    keycloakInstance.onTokenExpired = refresh;
    document.addEventListener('visibilitychange', handleVisibilityChange);
    // Сеть вернулась: прошлые попытки могли не дойти до Keycloak
    globalThis.addEventListener('online', refresh);

    return () => {
      clearInterval(refreshInterval);
      keycloakInstance.onTokenExpired = undefined;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      globalThis.removeEventListener('online', refresh);
    };
  }, [keycloakInstance]);

  if (isLoading) {
    return (
      <Fade in>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Fade>
    );
  }

  if (isError) {
    return (
      <Fade in>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box sx={{ maxWidth: 500, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Произошла ошибка при загрузке приложения. <br />
              Возможно, у вас нет доступа.
            </Typography>

            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                keycloakInstance?.logout();
                localStorage.removeItem(ACCESS_TOKEN_KEY);
                localStorage.removeItem(REFRESH_TOKEN_KEY);
              }}
            >
              Выйти
            </Button>
          </Box>
        </Box>
      </Fade>
    );
  }

  const handleAcceptAgreement = async (): Promise<void> => {
    await acceptAgreement();

    // Получаем обновленные данные пользователя
    const updatedUser = await getCurrentUser();

    // Теперь загружаем операции, так как пользователь принял соглашение
    const operations = await getOperations();

    dispatch(ASetUser(updatedUser));
    dispatch(ASetOperations(operations));
    setShowAgreementDialog(false);
  };

  return (
    <SessionContext.Provider value={{ keycloak: keycloakInstance, isAuthenticated }}>
      {children}
      <UserAgreementDialog open={showAgreementDialog} onAccept={handleAcceptAgreement} />
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);

  if (!context) throw new Error('useSession должен использоваться внутри SessionProvider');

  return context;
};

export default SessionProvider;
